import { CloseOutlined, QrCodeOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Step as MUIStep,
  StepConnector,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import { getCurrentUser, setUpTOTP, updateMFAPreference, verifyTOTPSetup } from 'aws-amplify/auth';
import SixCodeTextField from 'components/SixCodeTextField/SixCodeTextField';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useCallback, useState } from 'react';
import QRCode from 'react-qr-code';
import styled from 'styled-components';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

interface Props {
  handleClose: () => void;
}

const StyledStepConnector = styled(StepConnector)`
  & span {
    height: 100%;
  }
`;

enum Step {
  METHOD_OF_AUTHENTICATION_STEP,
  SETUP_STEP,
  VERIFICATION_STEP
}

const TwoFactorAuthentication: React.FC<React.PropsWithChildren<Props>> = ({ handleClose }) => {
  const { translation } = useTranslation();
  const [activeStep, setActiveStep] = useState<Step>(Step.METHOD_OF_AUTHENTICATION_STEP);
  const [qrCodeURL, setQrCodeURL] = useState<URL>();
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  const verifyTOTP = useCallback(async (token: string): Promise<void> => {
    if (!loading && !isComplete) {
      setLoading(true);
      try {
        await verifyTOTPSetup({ code: token });
        await updateMFAPreference({ totp: 'PREFERRED' });
        setIsComplete(true);
      } catch (err) {
        setError(true);
        setIsComplete(false);
        console.error(err instanceof Error ? err.message : 'Incorrect code');
      } finally {
        setLoading(false);
      }
    }
  }, []);

  const handleGoBackStep = () => {
    switch (activeStep) {
      case Step.SETUP_STEP:
        setActiveStep(Step.METHOD_OF_AUTHENTICATION_STEP);
        break;
      case Step.VERIFICATION_STEP:
        setActiveStep(Step.SETUP_STEP);
        break;
      case Step.METHOD_OF_AUTHENTICATION_STEP:
      default:
        break;
    }
  };

  const handleChooseMethodOfAuthentication = async () => {
    try {
      const user = await getCurrentUser();
      const secretCode = await setUpTOTP();
      if (secretCode) {
        const url = secretCode.getSetupUri('ATLAS', user.signInDetails?.loginId);
        setQrCodeURL(url);
        setActiveStep(Step.SETUP_STEP);
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : 'Error while configuring TOTP');
    }
  };

  const handleGoToVerifyStep = () => {
    setActiveStep(Step.VERIFICATION_STEP);
  };

  const steps = [translation.method_of_authentication, translation.setup, translation.verification];

  const MethodOfAuthenticationStep = (
    <ComponentWrapper margin="24px 16px">
      <ComponentWrapper
        padding="16px"
        width="504px"
        height="72px"
        gap="16px"
        style={{
          border: '1px solid rgba(204, 204, 0, 1)',
          borderRadius: '4px',
          backgroundColor: 'rgba(197, 216, 0, 0.08)'
        }}
        alignItems="center"
      >
        <QrCodeOutlined width={40} style={{ color: 'rgba(204, 204, 0, 1)' }} />
        <Typography variant="body1" color="textPrimary">
          {translation.authenticator_app}
        </Typography>
      </ComponentWrapper>
    </ComponentWrapper>
  );

  const SetupStep = (
    <ComponentWrapper
      padding="1rem 1rem 1rem 1rem"
      width="504px"
      alignItems="center"
      gap="1.5rem"
      flexDirection="column"
    >
      <Typography variant="body2" color="textPrimary">
        {translation.open_or_install_an_authenticator_app_to_your_liking_and_use_it_to_scan_the_qr_code_shown_below}
      </Typography>
      {qrCodeURL && <QRCode value={'' + qrCodeURL} />}
    </ComponentWrapper>
  );

  const VerificationStep = (
    <ComponentWrapper
      padding="1rem 1rem 1rem 1rem"
      width="504px"
      alignItems="center"
      gap="1.5rem"
      flexDirection="column"
    >
      <Typography variant="body2" color="textPrimary">
        {translation.please_enter_the_code_from_your_authenticator_app_below_to_finish_the_setup}
      </Typography>
      <SixCodeTextField
        code={code}
        setCode={setCode}
        setError={setError}
        triggerWhenCodeIsFull={() => verifyTOTP(code.join(''))}
      />
      {error && (
        <Alert
          variant="standard"
          severity={'error'}
          style={{ backgroundColor: 'rgba(211, 47, 47, 0.08)', border: '1px solid #D32F2F' }}
          sx={{
            backgroundColor: 'rgba(211, 47, 47, 0.08)',
            '& .MuiSvgIcon-root': {
              color: '#F44336'
            },
            '& .MuiAlert-message': {
              color: 'rgba(244, 199, 199, 1)'
            }
          }}
        >
          <ComponentWrapper flexDirection="column">
            <Typography color="rgba(244, 199, 199, 1)" variant="h6">
              {translation.invalid_verification_code}
            </Typography>
            <Typography variant="body2" color="rgba(244, 199, 199, 1)">
              {
                translation.please_check_if_the_entered_code_is_correct_or_wait_until_a_new_code_is_available_in_your_app
              }
            </Typography>
          </ComponentWrapper>
        </Alert>
      )}
    </ComponentWrapper>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case Step.SETUP_STEP:
        return SetupStep;
      case Step.VERIFICATION_STEP:
        return VerificationStep;
      case Step.METHOD_OF_AUTHENTICATION_STEP:
      default:
        return MethodOfAuthenticationStep;
    }
  };

  const MethodOfAuthenticationStepButton = (
    <Button
      id="button-choose-method-of-authentication"
      size="medium"
      variant="contained"
      color="primary"
      onClick={handleChooseMethodOfAuthentication}
    >
      {translation.next}
    </Button>
  );

  const SetupStepButton = (
    <ComponentWrapper gap="16px">
      <Button
        id="go-back-button"
        size="medium"
        variant="text"
        color="inherit"
        style={{ color: 'rgba(255, 255, 255, 0.87)' }}
        onClick={handleGoBackStep}
      >
        {translation.back}
      </Button>

      <Button
        id="button-go_to_verify_step"
        size="medium"
        variant="contained"
        color="primary"
        onClick={handleGoToVerifyStep}
      >
        {translation.next}
      </Button>
    </ComponentWrapper>
  );

  const VerificationStepButton = (
    <ComponentWrapper gap="16px">
      <Button
        id="go-back-button"
        size="medium"
        variant="text"
        color="inherit"
        style={{ color: 'rgba(255, 255, 255, 0.87)' }}
        onClick={handleGoBackStep}
      >
        {translation.back}
      </Button>
      <Button
        id="button-complete"
        size="medium"
        variant="contained"
        color="primary"
        onClick={handleClose}
        disabled={!isComplete}
      >
        {translation.complete}
      </Button>
    </ComponentWrapper>
  );

  const getStepButton = () => {
    switch (activeStep) {
      case Step.SETUP_STEP:
        return SetupStepButton;
      case Step.VERIFICATION_STEP:
        return VerificationStepButton;
      case Step.METHOD_OF_AUTHENTICATION_STEP:
      default:
        return MethodOfAuthenticationStepButton;
    }
  };

  return (
    <Box
      id="two-factor-authentication-modal"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#252525',
        border: '1px solid #444',
        borderRadius: '4px'
      }}
    >
      <ComponentWrapper width="728px" flexDirection="column">
        <ComponentWrapper padding="1rem" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" color="textPrimary">
            {translation.two_factor_authentication}
          </Typography>
          <IconButton onClick={handleClose} style={{ padding: '4px' }}>
            <CloseOutlined style={{ color: 'rgba(255, 255, 255, 0.56)' }} />
          </IconButton>
        </ComponentWrapper>
        <Divider />
        <ComponentWrapper height="17rem">
          <Stepper
            connector={<StyledStepConnector />}
            activeStep={activeStep}
            orientation="vertical"
            style={{
              height: '17rem',
              maxHeight: '17rem',
              minHeight: '17rem',
              padding: '1.5rem 1rem 1rem 1rem',
              width: '10.5rem',
              gap: '0.5rem',
              alignItems: 'stretch'
            }}
          >
            {steps.map((step) => (
              <MUIStep key={step}>
                <StepLabel>{step}</StepLabel>
              </MUIStep>
            ))}
          </Stepper>
          <Divider orientation="vertical" />
          <ComponentWrapper>{getStepContent()}</ComponentWrapper>
        </ComponentWrapper>
        <Divider />
        <ComponentWrapper justifyContent="end" height="4rem" padding="0.75rem 1rem">
          {getStepButton()}
        </ComponentWrapper>
      </ComponentWrapper>
    </Box>
  );
};

export default TwoFactorAuthentication;
